import { simplify } from "@uri/silly-nlp";
import { anyjuxt, empty, filter, greater, isPromise, pipe } from "gamla";
import { compareTwoStrings } from "string-similarity";

export const truncate = (length: number) => (s: string): string =>
  s.slice(0, length);

export const escapeRegex = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

export const extractBaseTitle = (name: string): string => {
  const parts = name.split(/[:–\-(]/);
  const first = parts[0]?.trim() ?? "";
  if (first.length >= 4) return first;
  const second = parts[1]?.trim() ?? "";
  const combined = `${first}: ${second}`.trim();
  if (combined.length >= 4) return combined;
  return name.trim();
};

// Streams an offset-paginated dataset page by page so callers never
// materialize the whole dataset in memory. Stops on a short or empty page.
export const paginateOffset = <T>(
  pageSize: number,
  fetchPage: (offset: number) => Promise<T[]>,
  handlePage: (page: T[]) => unknown,
  offset: number,
): Promise<void> =>
  fetchPage(offset).then(async (page) => {
    if (empty(page)) return;
    await handlePage(page);
    if (page.length === pageSize) {
      return paginateOffset(pageSize, fetchPage, handlePage, offset + pageSize);
    }
  });

// deno-lint-ignore no-explicit-any
type UnaryAsync = (x: any) => Promise<any>;

export const pairRightAndApply = <
  F extends UnaryAsync,
  // deno-lint-ignore no-explicit-any
  G extends (x: Parameters<F>[0], r: Awaited<ReturnType<F>>) => any,
>(
  f: F,
  g: G,
) =>
async (x: Parameters<F>[0]): Promise<Awaited<ReturnType<G>>> =>
  g(x, await f(x));

// deno-lint-ignore no-explicit-any
export const findOrRaise = <T>(predicate: (t: T) => any) => (xs: T[]) => {
  for (const x of xs) if (predicate(x)) return x;
  throw new Error("No element found");
};

const mb = (bytes: number) => (bytes / (1024 * 1024)).toFixed(1);

export const heapMb = () => mb(Deno.memoryUsage().heapUsed);

// Off-heap allocations show up in rss/external rather than heapUsed, so a spike
// in either points at buffers (image bytes, response bodies) that heapMb alone
// would hide.
export const memFields = () => {
  const { rss, heapTotal, heapUsed, external } = Deno.memoryUsage();
  return {
    heapMb: mb(heapUsed),
    heapTotalMb: mb(heapTotal),
    rssMb: mb(rss),
    extMb: mb(external),
  };
};

export const memUsage = () =>
  Object.entries(memFields())
    .map(([key, value]) => `${key}=${value}`)
    .join(" ");

const logEnd = (txt: string, started: number) => {
  console.log(
    `Ended ${txt}. Took ${
      ((Date.now() - started) / 1000).toFixed(3)
    } seconds. ${memUsage()}`,
  );
};

export const logStage =
  // deno-lint-ignore no-explicit-any
  (txt: string) => <T extends (...xs: any[]) => any>(f: T): T =>
    ((...xs: Parameters<T>) => {
      const started = Date.now();
      console.log(`Started ${txt} at ${started}`);
      const result: ReturnType<T> = f(...xs);
      if (isPromise(result)) {
        return result.then((x: Awaited<ReturnType<T>>) => {
          logEnd(txt, started);
          return x;
        });
      }
      logEnd(txt, started);
      return result;
    }) as T;

export const logStageSamplingHeap = (txt: string, everyMs = 150) =>
// deno-lint-ignore no-explicit-any
<T extends (...xs: any[]) => any>(f: T): T =>
  ((...xs: Parameters<T>) => {
    const started = Date.now();
    console.log(`Started ${txt} at ${started} ${memUsage()}`);
    const samplerId = setInterval(
      () =>
        console.log(
          `heap-sample stage="${txt}" ms=${Date.now() - started} ${memUsage()}`,
        ),
      everyMs,
    );
    const stop = () => clearInterval(samplerId);
    const result: ReturnType<T> = f(...xs);
    if (isPromise(result)) {
      return result.then(
        (x: Awaited<ReturnType<T>>) => {
          stop();
          logEnd(txt, started);
          return x;
        },
        (e: unknown) => {
          stop();
          throw e;
        },
      );
    }
    stop();
    logEnd(txt, started);
    return result;
  }) as T;

export const objectKeys = <K extends string | number | symbol>(
  o: Partial<Record<K, unknown>>,
) => Object.keys(o) as K[];

export const valFilterSync = <K extends string | number | symbol, V>(
  // deno-lint-ignore no-explicit-any
  predicate: (value: V) => any,
) =>
(o: Partial<Record<K, V>>): Record<K, V> =>
  // @ts-expect-error: not sure how to type this.
  pipe(
    // @ts-expect-error: not sure how to type this.
    Object.entries,
    filter(([, value]: [K, V]) => predicate(value)),
    Object.fromEntries,
    // @ts-expect-error: not sure how to type this.
  )(o);

export const clusters =
  <T>(equivalence: (x: T, y: T) => boolean) => (items: T[]) => {
    const clusters: T[][] = [];
    for (const item of items) {
      const newCluster = [item];
      for (let i = 0; i < clusters.length; i++) {
        const cluster = clusters[i];
        if (cluster.some((elem) => equivalence(item, elem))) {
          cluster.forEach((elem) => {
            newCluster.push(elem);
          });
          clusters.splice(i, 1);
          i--;
          break;
        }
      }
      clusters.push(newCluster);
    }
    return clusters;
  };

const hebrewToPhoneticChar = (c: string): string => {
  if (c === "א" || c === "ע" || c === "ה" || c === "י" || c === "ו") return "";
  if (c === "ב") return "v";
  if (c === "ג") return "g";
  if (c === "ד") return "d";
  if (c === "ז") return "z";
  if (c === "ח" || c === "כ" || c === "ך") return "ch";
  if (c === "ט" || c === "ת") return "t";
  if (c === "ל") return "l";
  if (c === "מ" || c === "ם") return "m";
  if (c === "נ" || c === "ן") return "n";
  if (c === "ס") return "s";
  if (c === "פ" || c === "ף") return "f";
  if (c === "צ" || c === "ץ") return "tz";
  if (c === "ק") return "k";
  if (c === "ר") return "r";
  if (c === "ש") return "sh";
  return c;
};

const cleanStopWordsAndPrefixes = (s: string): string =>
  s
    .toLowerCase()
    .trim()
    .replace(/\b(in|at|the|and|of|with|a|an|on|to|for|by|from)\b/g, " ")
    .split(/\s+/)
    .map((word) =>
      word.length >= 4 && /^[בהלו]/.test(word) && /[\u0590-\u05FF]/.test(word)
        ? word.slice(1)
        : word
    )
    .join(" ");

const convertToPhoneticEnglish = (s: string): string =>
  [...s]
    .map((c) => (/[\u0590-\u05FF]/.test(c) ? hebrewToPhoneticChar(c) : c))
    .join("");

const mapEnglishLetters = (s: string): string =>
  [...s]
    .map((c) => {
      if (c === "b" || c === "w") return "v";
      if (c === "c" || c === "q") return "k";
      if (c === "p") return "f";
      return c;
    })
    .join("");

const mapEnglishPhonemes = (s: string): string =>
  s
    .replace(/sh/g, "S")
    .replace(/ch/g, "C")
    .replace(/kh/g, "C")
    .replace(/ph/g, "f")
    .replace(/tz/g, "Z")
    .replace(/ts/g, "Z")
    .replace(/th/g, "t");

const restorePlaceholders = (s: string): string =>
  s
    .replace(/S/g, "sh")
    .replace(/C/g, "ch")
    .replace(/Z/g, "tz");

const removeVowelsAndSilent = (s: string): string =>
  s.replace(/[aeiouyh]/g, "");

const collapseDuplicates = (s: string): string => s.replace(/(.)\1+/g, "$1");

export const toPhonetic = (s: string): string => {
  const clean = cleanStopWordsAndPrefixes(s);
  const withPhoneticEng = convertToPhoneticEnglish(clean);
  const mappedPhonemes = mapEnglishPhonemes(withPhoneticEng);
  const mappedLetters = mapEnglishLetters(mappedPhonemes);
  const removedVowels = removeVowelsAndSilent(mappedLetters);
  const restored = restorePlaceholders(removedVowels);
  return collapseDuplicates(restored).trim().replace(/\s+/g, " ");
};

export const simplifyToPhonetic = pipe(simplify, toPhonetic);

const hasWordContained = (shortStr: string, longStr: string): boolean =>
  shortStr.split(/\s+/).filter(Boolean).length >= 2 &&
  new RegExp(`(?:^|\\s)${escapeRegex(shortStr)}(?:\\s|$)`).test(longStr);

const wordsContained = (x: string, y: string): boolean =>
  x.length <= y.length ? hasWordContained(x, y) : hasWordContained(y, x);

export const similarString = (threshold: number) =>
  pipe(
    simplifyToPhonetic,
    (x: string) =>
      pipe(
        simplifyToPhonetic,
        anyjuxt(
          (y: string) => x === y,
          (y: string) => wordsContained(x, y),
          pipe(
            (y: string) => compareTwoStrings(x, y) as number,
            greater(threshold),
          ),
        ),
      ),
  );

export const coerceDatetimeString = (
  s: string | null | undefined,
): string | null => {
  if (s === null || s === undefined) return null;
  return s.replace(/["']/g, "").trim();
};

export const decodeMimeWords = (str: string): string => {
  const regex = /=\?([A-Za-z0-9_-]+)\?([QqBb])\?([^? \t]+)\?=/g;
  return str.replace(regex, (match, charset, encoding, encodedText) => {
    try {
      if (encoding.toUpperCase() === "Q") {
        const qText = encodedText.replace(/_/g, " ");
        const percentEncoded = qText.replace(/=([0-9A-Fa-f]{2})/g, "%$1");
        try {
          return decodeURIComponent(percentEncoded);
        } catch {
          const bytes: number[] = [];
          for (let i = 0; i < qText.length;) {
            if (qText[i] === "=" && i + 2 < qText.length) {
              const hex = qText.slice(i + 1, i + 3);
              bytes.push(parseInt(hex, 16));
              i += 3;
            } else {
              bytes.push(qText.charCodeAt(i));
              i++;
            }
          }
          return new TextDecoder(charset).decode(new Uint8Array(bytes));
        }
      } else if (encoding.toUpperCase() === "B") {
        const binaryString = atob(encodedText);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        return new TextDecoder(charset).decode(bytes);
      }
    } catch (e) {
      console.error("Failed to decode MIME word", match, e);
    }
    return match;
  });
};

const hourInMs = 60 * 60 * 1000;
const dayInMs = 24 * hourInMs;

export const normalizeEventDuration = (
  start: number,
  rawEnd: number,
  categories: string[] = [],
): { start: number; end: number } => {
  const isExhibition = categories.includes("exhibition");
  const isFestivalOrRetreat = categories.includes("festival") ||
    categories.includes("retreat");
  const maxDurationMs = isExhibition
    ? 180 * dayInMs
    : isFestivalOrRetreat
    ? 14 * dayInMs
    : 36 * hourInMs;
  const defaultDurationMs = isFestivalOrRetreat ? 24 * hourInMs : 4 * hourInMs;
  const isValidEnd = !isNaN(rawEnd) &&
    rawEnd > start &&
    (rawEnd - start) <= maxDurationMs;
  const end = isValidEnd ? rawEnd : start + defaultDurationMs;
  return { start, end };
};
